import { createSelector } from '@ngrx/store';
import {
  BookedPackageTotalPriceGroupedByPackageIndex,
  BookedPackageTotalPriceGroupedByPackageNumber,
  BookingPackageType,
  BookingProductType,
  BookingTariff,
  BookingTariffType,
  isBookingProductTypePackage,
  isBookingProductTypeTariff
} from '@products/models/booking.model';
import { RemovePackageBooking } from '@products/models/products.model';
import { getProductBookingState } from '@store/products/product.selectors';
import { cloneDeep, isEmpty } from 'lodash';

/**
 * Get booked package tariff types
 * @description Get booked packages tariff types by ticket type id
 * @param ticketTypeId
 * @returns Booked tariff types or empty array
 */
export const getBookedPackageTariffTypesByTicketTypeId = (ticketTypeId: number) =>
  createSelector(getProductBookingState, (productBookingState): BookingTariffType[] => {
    const bookedTariffTypes: BookingTariffType[] = [];

    productBookingState.list.forEach((bookedProductType) => {
      if (isBookingProductTypePackage(bookedProductType)) {
        bookedProductType.productTypes.forEach((packageProductType) => {
          if (isBookingProductTypeTariff(packageProductType)) {
            if (packageProductType.ticketTypeId === ticketTypeId) {
              bookedTariffTypes.push(packageProductType);
            }
          }
        });
      }
    });

    return bookedTariffTypes;
  });

/**
 * Get all booked packages
 * @description Get all booked packages
 * @returns Booked package types or empty array
 */
export const getBookedPackages = createSelector(getProductBookingState, (productBookingState): BookingPackageType[] => {
  const bookedPackageTypes: BookingPackageType[] = [];

  productBookingState.list.forEach((bookedProductType) => {
    if (isBookingProductTypePackage(bookedProductType)) {
      bookedPackageTypes.push(bookedProductType);
    }
  });

  return bookedPackageTypes;
});

/**
 * Get booked packages by package number
 * @description Get all booked package types by specified package number
 * @param packageNumber
 * @returns Booked package types or empty array
 */
export const getBookedPackagesByPackageNumber = (packageNumber: number) =>
  createSelector(
    getBookedPackages,
    (bookingPackageTypes): BookingPackageType[] =>
      bookingPackageTypes.filter((bookedPackageType) => bookedPackageType.packageNumber === packageNumber) || []
  );

/**
 * Get booked package by package number and index
 * @description Get all booked packages by specified package number and index
 * @param packageNumber
 * @param packageIndex
 * @returns Booked package type or null
 */
export const getBookedPackageByPackageNumberAndIndex = (packageNumber: number, packageIndex: number) =>
  createSelector(
    getBookedPackagesByPackageNumber(packageNumber),
    (bookedPackageTypes): BookingPackageType =>
      bookedPackageTypes.find((bookedPackageType) => bookedPackageType.packageIndex === packageIndex) || null
  );

/**
 * Get booked packages by package number and indexes
 * @description Get booked packages with specified package indexes
 * @param removePackageBooking
 * @returns Booked package types or empty array
 */
export const getBookedPackageTypeByPackageNumberAndIndexes = (removePackageBooking: RemovePackageBooking) =>
  createSelector(
    getBookedPackagesByPackageNumber(removePackageBooking.packageNumber),
    (bookedPackages): BookingPackageType[] =>
      bookedPackages.filter((bookedPackage) => removePackageBooking.packageIndexes.includes(bookedPackage.packageIndex))
  );

/**
 * Get package product type by package and ticketPersonId
 * @description Get package product type by package number, index and ticketPersonId in package tariff
 * @param packageNumber
 * @param packageIndex
 * @param ticketPersonId
 * @returns Booked package or null
 */
export const getBookedPackageTypeByPackageAndTicketPersonId = (
  packageNumber: number,
  packageIndex: number,
  ticketTypeId: number,
  ticketPersonId: number
) =>
  createSelector(
    getBookedPackageByPackageNumberAndIndex(packageNumber, packageIndex),
    (bookedPackage): BookingPackageType => {
      if (!bookedPackage) {
        return null;
      }

      const bookedPackageClone: BookingPackageType = cloneDeep(bookedPackage);
      const bookedPackageTariffType = bookedPackageClone.productTypes.find((bookedProductType) => {
        if (isBookingProductTypeTariff(bookedProductType)) {
          return bookedProductType.tariffs.some(
            (packageTariff) =>
              packageTariff.ticketTypeId === ticketTypeId && packageTariff.ticketPersonId === ticketPersonId
          );
        }
      });

      if (isBookingProductTypeTariff(bookedPackageTariffType)) {
        bookedPackageTariffType.tariffs = bookedPackageTariffType.tariffs.filter(
          (bookedPackageTariff) => bookedPackageTariff.ticketPersonId === ticketPersonId
        );

        bookedPackageClone.productTypes = [bookedPackageTariffType];
      } else {
        return null;
      }

      return bookedPackageClone;
    }
  );

/**
 * Get booked package product types
 * @description Get package product types by specified package number and index
 * @param packageNumber
 * @param packageIndex
 * @returns Booked package product types or empty array
 */
export const getBookedPackageProductTypesByPackageNumberAndIndex = (packageNumber: number, packageIndex: number) =>
  createSelector(
    getBookedPackageByPackageNumberAndIndex(packageNumber, packageIndex),
    (bookedPackage): BookingProductType[] => {
      if (!bookedPackage) {
        return [];
      }

      return bookedPackage.productTypes;
    }
  );

/**
 * Get booked package tariff types
 * @description Get booked package tariff types by specified package number and index
 * @param packageNumber
 * @param packageIndex
 * @returns Booked package tariff types or empty array
 */
export const getBookedPackageTariffTypesByPackageNumberAndIndex = (packageNumber: number, packageIndex: number) =>
  createSelector(
    getBookedPackageProductTypesByPackageNumberAndIndex(packageNumber, packageIndex),
    (bookedPackageProductTypes): BookingTariffType[] => {
      if (!bookedPackageProductTypes.length) {
        return [];
      }

      const bookedPackageTariffTypes: BookingTariffType[] = [];

      bookedPackageProductTypes
        .filter(isBookingProductTypeTariff)
        .forEach((bookedPackageTariffType) => bookedPackageTariffTypes.push(bookedPackageTariffType));

      return bookedPackageTariffTypes;
    }
  );

/**
 * Get booked package tariffs
 * @description Get booked package tariffs by specified package number and index
 * @param packageNumber
 * @param packageIndex
 * @returns Booked tariffs or empty array
 */
export const getBookedPackageTariffsByPackageNumberAndIndex = (packageNumber: number, packageIndex: number) =>
  createSelector(
    getBookedPackageTariffTypesByPackageNumberAndIndex(packageNumber, packageIndex),
    (bookedPackageTariffTypes): BookingTariff[] => {
      const bookedPackageTariffs: BookingTariff[] = [];

      bookedPackageTariffTypes.forEach((bookedPackageTariffType) =>
        bookedPackageTariffType.tariffs.forEach((bookedPackageTariff) => bookedPackageTariffs.push(bookedPackageTariff))
      );

      return bookedPackageTariffs;
    }
  );

/**
 * Get booked package ticket
 * @description Get booked package tariff by specified package number, index and ticket person id
 * @param packageNumber
 * @param packageIndex
 * @param ticketPersonId
 * @returns Booked tariff or null
 */
export const getBookedPackageTariffByTicketPersonId = (
  packageNumber: number,
  packageIndex: number,
  ticketPersonId: number
) =>
  createSelector(
    getBookedPackageTariffsByPackageNumberAndIndex(packageNumber, packageIndex),
    (bookedPackageTariffs): BookingTariff =>
      bookedPackageTariffs.find((bookedPackageTariff) => bookedPackageTariff.ticketPersonId === ticketPersonId) || null
  );

/**
 * Get booked package tariffs
 * @description Get package booking tariffs (regular tariff not included)
 * @returns Booked package tariff or empty array
 */
export const getBookedPackageTariffs = createSelector(
  getBookedPackages,
  (bookedPackageTypes: BookingPackageType[]): BookingTariff[] => {
    const bookedTariffs: BookingTariff[] = [];

    bookedPackageTypes.forEach((bookedPackageType) => {
      bookedPackageType.productTypes.forEach((bookedPackageProductType) => {
        if (isBookingProductTypeTariff(bookedPackageProductType)) {
          bookedPackageProductType.tariffs.forEach((bookedPackageTariff) => bookedTariffs.push(bookedPackageTariff));
        }
      });
    });

    return bookedTariffs;
  }
);

/**
 * Get single package price summed up
 * @description Get booked package tariffs by index and number and sum their price * count
 * @param packageNumber
 * @param packageIndex
 * @returns Package price summed up
 */
export const getBookedPackagePriceByPackageNumberAndPackageIndex = (packageNumber: number, packageIndex: number) =>
  createSelector(
    getBookedPackageTariffsByPackageNumberAndIndex(packageNumber, packageIndex),
    (bookedPackageTariffs: BookingTariff[]): number =>
      bookedPackageTariffs.reduce(
        (packageTotal, bookedPackageTariff) =>
          packageTotal +
          bookedPackageTariff.count *
            (!isEmpty(bookedPackageTariff.promoCodeDiscounts)
              ? bookedPackageTariff.discountedPrice
              : bookedPackageTariff.price),
        0
      )
  );

/**
 * Get booked packages count by package number
 * @description Get booked packages in package type array by package number
 * @param packageNumber
 * @returns Number of booked packages
 */
export const getBookedPackagesCount = (packageNumber: number) =>
  createSelector(getBookedPackagesByPackageNumber(packageNumber), (bookedPackages): number => bookedPackages.length);

/**
 * Get all grouped package total price
 * @description Get all packages total price grouped by package index
 * @returns Object with key as package index and value as package total price
 */
export const getAllBookedPackagePricesGroupedByPackageIndex = createSelector(
  getBookedPackageTariffs,
  (allBookedPackageTariffs: BookingTariff[]): BookedPackageTotalPriceGroupedByPackageIndex => {
    const packagePricesByPackageIndex: BookedPackageTotalPriceGroupedByPackageIndex = {};

    allBookedPackageTariffs.forEach((packageTariff) => {
      const { packageIndex, count, price, discountedPrice, promoCodeDiscounts, workshops } = packageTariff;
      const packageBookingTariffTotalPrice = count * price;
      const packageBookingTariffDiscountedPrice = isEmpty(promoCodeDiscounts) ? price : discountedPrice;
      const packageBookingTariffTotalDiscountedPrice = count * packageBookingTariffDiscountedPrice;

      if (!packagePricesByPackageIndex[packageIndex]) {
        packagePricesByPackageIndex[packageIndex] = {
          totalPrice: packageBookingTariffTotalPrice,
          totalDiscountedPrice: packageBookingTariffTotalDiscountedPrice,
          hasPromoCodeDiscount: !isEmpty(promoCodeDiscounts)
        };
      } else {
        packagePricesByPackageIndex[packageIndex].totalPrice += packageBookingTariffTotalPrice;
        packagePricesByPackageIndex[packageIndex].totalDiscountedPrice += packageBookingTariffTotalDiscountedPrice;
        packagePricesByPackageIndex[packageIndex].hasPromoCodeDiscount =
          packagePricesByPackageIndex[packageIndex].hasPromoCodeDiscount || !isEmpty(promoCodeDiscounts);
      }

      const workshopsTotalPrices = workshops.reduce(
        (workshopsTotalPrices, { tariffUuids, price: workshopPrice }) =>
          workshopsTotalPrices + tariffUuids.length * workshopPrice,
        0
      );

      packagePricesByPackageIndex[packageIndex].totalPrice += workshopsTotalPrices;
      packagePricesByPackageIndex[packageIndex].totalDiscountedPrice += workshopsTotalPrices;
    });

    return packagePricesByPackageIndex;
  }
);

/**
 * Get all grouped packages subtotal price
 * @description Get all packages subtotal total price grouped by package number
 * @returns Object with key as package index and value as package subtotal price
 */
export const getAllBookedPackagePricesGroupedByPackageNumber = createSelector(
  getBookedPackageTariffs,
  (allBookedPackageTariffs: BookingTariff[]): BookedPackageTotalPriceGroupedByPackageNumber => {
    const packagePricesByPackageNumber: BookedPackageTotalPriceGroupedByPackageNumber = {};

    allBookedPackageTariffs.forEach((packageTariff) => {
      const { packageNumber, count, price, discountedPrice, promoCodeDiscounts, workshops } = packageTariff;
      const packageBookingTariffTotalPrice = count * price;
      const packageBookingTariffDiscountedPrice = isEmpty(promoCodeDiscounts) ? price : discountedPrice;
      const packageBookingTariffTotalDiscountedPrice = count * packageBookingTariffDiscountedPrice;

      if (!packagePricesByPackageNumber[packageNumber]) {
        packagePricesByPackageNumber[packageNumber] = {
          totalPrice: packageBookingTariffTotalPrice,
          totalDiscountedPrice: packageBookingTariffTotalDiscountedPrice
        };
      } else {
        packagePricesByPackageNumber[packageNumber].totalPrice += packageBookingTariffTotalPrice;
        packagePricesByPackageNumber[packageNumber].totalDiscountedPrice += packageBookingTariffTotalDiscountedPrice;
      }

      const workshopsTotalPrices = workshops.reduce(
        (workshopsTotalPrices, { tariffUuids, price: workshopPrice }) =>
          workshopsTotalPrices + tariffUuids.length * workshopPrice,
        0
      );

      packagePricesByPackageNumber[packageNumber].totalPrice += workshopsTotalPrices;
      packagePricesByPackageNumber[packageNumber].totalDiscountedPrice += workshopsTotalPrices;
    });

    return packagePricesByPackageNumber;
  }
);
